import {
  faBars,
  faCog,
  faCompressAlt,
  faExpandAlt,
  faSignInAlt,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, lazy, Suspense, useState } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { OptionalAuthorizationServiceRequest } from 'src/apis/authorization-service/use-authorization-service';
import { ActionModeProvider } from 'src/components/action-mode';
import { Button } from 'src/components/buttons-and-actions/button';
import { IconDropdown } from 'src/components/buttons-and-actions/icon-dropdown';
import { Container } from 'src/components/layout/container';
import {
  HeaderPrimaryBackground,
  HeaderPrimaryCenter,
} from 'src/components/layout/header/primary-nav';
import { ActiveSection } from 'src/components/layout/header/types';
import { MobileNavState } from 'src/components/layout/header/use-mobile-nav';
import {
  UserMenu,
  UserMenuProps,
} from 'src/components/layout/header/user-menu';
import { Stack } from 'src/components/layout/stack';
import { LoginModal } from 'src/components/login-modal';
import { Logo } from 'src/components/media-and-icons/logo';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useCognitoUser } from 'src/hooks/use-cognito';
import { useFeatureFlags } from 'src/hooks/use-feature-flags';
import { useFullwidth } from 'src/hooks/use-fullwidth';
import { useGmdFlags } from 'src/hooks/use-gdm-flags';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import { OrganisationServiceRequest } from 'src/pages/settings/organisation/use-organisation-service';
import { Colors } from 'src/styles';
import { isLng } from 'src/utils/is-lng';
import styled from 'styled-components';

const MobileNav = styled.div`
  display: grid;
  grid-gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 0.1rem solid ${Colors.brand};
`;

const DeveloperToolsIcon = lazy(() => import('src/pages/developer-tools/icon'));

type Props = {
  activeSection: ActiveSection;
  authorizationService?: Successful<OptionalAuthorizationServiceRequest>;
  organisationService?: Successful<OrganisationServiceRequest>;
  mobileNav: MobileNavState;
};

export const HeaderPrimary: FC<Props> = ({
  activeSection,
  authorizationService,
  organisationService,
  mobileNav,
}) => {
  const [fullwidth, setFullwidth] = useFullwidth();
  const [showLogin, setShowLogin] = useState(false); // needed for KON
  const { minTablet, minDesktop, minWideDesktop } = useBreakpoints();
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const cognitoUser = useCognitoUser();
  const isAuthenticated = Boolean(monolithUser || cognitoUser.data);
  const featureFlags = useFeatureFlags();
  const gdmFlags = useGmdFlags();
  const isGdmSection = useRouteMatch('/gdm');
  const { pathname, search } = useLocation();
  const [reauthenticate] = useReauthenticate();

  const userMenuData: Omit<UserMenuProps, 'show'> = {
    organisationService: organisationService?.response.data ?? null,
    authorizationService: authorizationService?.response.data ?? null,
  };

  const sectionLinks = (
    <>
      {(!monolithUser || monolithUser.role !== 'SSO_ADMIN') && (
        <ExternalLink
          mode="primary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/auctions`}
          isActive={activeSection.transport}
        >
          Transport
        </ExternalLink>
      )}

      {(!monolithUser ||
        (monolithUser.isSso ? !isLng(monolithUser) : !monolithUser.isTso)) && (
        <Link
          mode="primary"
          to="/storage/offers"
          isActive={activeSection.storage}
        >
          Storage
        </Link>
      )}

      {(!monolithUser ||
        (monolithUser.isSso ? isLng(monolithUser) : !monolithUser.isTso)) && (
        <ExternalLink
          mode="primary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/lng/offers`}
          isActive={activeSection.lng}
        >
          LNG
        </ExternalLink>
      )}

      {(!monolithUser || !monolithUser.isSso) && (
        <Link mode="primary" to="/reporting" isActive={activeSection.reporting}>
          Reporting
        </Link>
      )}

      <Link mode="primary" to="/umm">
        UMM
      </Link>

      {(gdmFlags.userCanAcces || isGdmSection) && (
        <Link mode="primary" to="/gdm" isActive={activeSection.gdm}>
          GDM
        </Link>
      )}

      <Link mode="primary" to="/aggregate-eu">
        AggregateEU
      </Link>
    </>
  );

  const login = (
    <>
      {!reauthenticate && (
        <Link mode="primary" to="/registration/wizard/start">
          Register
        </Link>
      )}

      {featureFlags.authMigration ? (
        <Link
          mode={minTablet ? 'button-inverse' : 'primary'}
          to={
            // if someone presses on the login button from _within_ the login
            // section, we just want to reuse the search params
            pathname.startsWith('/login')
              ? { pathname: '/login', search }
              : {
                  pathname: '/login',
                  search: `redirectUrl=${encodeURIComponent(
                    location.href.replace('/angular', '/platform')
                  )}`,
                }
          }
        >
          {minTablet && !minDesktop ? (
            <FontAwesomeIcon icon={faSignInAlt} aria-label="Login" />
          ) : (
            <>Login</>
          )}
        </Link>
      ) : (
        <Button
          mode={minTablet ? 'inverse' : 'link-primary'}
          onClick={() => setShowLogin(true)}
        >
          {minTablet && !minDesktop ? (
            <FontAwesomeIcon icon={faSignInAlt} aria-label="Login" />
          ) : (
            <>Login</>
          )}
        </Button>
      )}
    </>
  );

  const developerTools = useDeveloperTools();

  return (
    <HeaderPrimaryBackground>
      <Container>
        <HeaderPrimaryCenter>
          <Stack flow="column" gap={minDesktop ? 3.4 : 2}>
            <RouterLink to="/platform/#/start">
              <Logo />
            </RouterLink>

            {minTablet && sectionLinks}
          </Stack>

          <Stack flow="column" gap={2}>
            {!minTablet && (
              <Button
                onClick={() =>
                  mobileNav.setValue(
                    mobileNav.value === 'primary' ? null : 'primary'
                  )
                }
              >
                <FontAwesomeIcon
                  icon={faBars}
                  aria-label="Toggle Main Navigation"
                />
              </Button>
            )}

            {minTablet &&
              (isAuthenticated && !reauthenticate ? (
                <>
                  <IconDropdown
                    label="Toggle Settings Menu"
                    data-testid="toggle-settings-menu"
                    icon={faCog}
                  >
                    <UserMenu
                      {...userMenuData}
                      show={{
                        personalSettings: false,
                        companySettings: true,
                        transportSettings: true,
                        storageSettings: true,
                        shipperSettings: true,
                        otherSettings: false,
                      }}
                    />
                  </IconDropdown>

                  <IconDropdown
                    label="Toggle User Menu"
                    data-testid="toggle-user-menu"
                    icon={faUser}
                  >
                    <UserMenu
                      {...userMenuData}
                      show={{
                        personalSettings: true,
                        companySettings: false,
                        transportSettings: false,
                        storageSettings: false,
                        shipperSettings: false,
                        otherSettings: true,
                      }}
                    />
                  </IconDropdown>
                </>
              ) : (
                login
              ))}

            {minWideDesktop && (
              <Button
                mode="icon-inverse"
                onClick={() => setFullwidth(!fullwidth)}
              >
                {fullwidth ? (
                  <FontAwesomeIcon
                    icon={faCompressAlt}
                    aria-label="Decrease width of website"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faExpandAlt}
                    aria-label="Increase width of website"
                  />
                )}
              </Button>
            )}
            {minWideDesktop && developerTools && (
              <Suspense fallback={<div style={{ width: 18 }} />}>
                <DeveloperToolsIcon />
              </Suspense>
            )}
          </Stack>
        </HeaderPrimaryCenter>
      </Container>

      {!minTablet && mobileNav.value === 'primary' && (
        <MobileNav onClick={() => mobileNav.setValue(null)}>
          <ActionModeProvider value={{ mode: 'link-primary' }}>
            {sectionLinks}
            {isAuthenticated && !reauthenticate ? (
              <UserMenu
                {...userMenuData}
                show={{
                  personalSettings: true,
                  companySettings: true,
                  transportSettings: true,
                  storageSettings: true,
                  shipperSettings: true,
                  otherSettings: true,
                }}
              />
            ) : (
              <>
                {!reauthenticate && (
                  <Link to="/registration/wizard/start">Register</Link>
                )}
                {featureFlags.authMigration ? (
                  <Link
                    to={
                      // if someone presses on the login button from _within_ the login
                      // section, we just want to reuse the search params
                      pathname.startsWith('/login')
                        ? { pathname: '/login', search }
                        : {
                            pathname: '/login',
                            search: `redirectUrl=${encodeURIComponent(
                              location.href
                            )}`,
                          }
                    }
                  >
                    Login
                  </Link>
                ) : (
                  <Button onClick={() => setShowLogin(true)}>Login</Button>
                )}
              </>
            )}
          </ActionModeProvider>
        </MobileNav>
      )}

      {showLogin && <LoginModal onDismiss={() => setShowLogin(false)} />}
    </HeaderPrimaryBackground>
  );
};
